import React from 'react';
import PropTypes from 'prop-types';

// Component Styles
import './tooltip.scss';

/**
 * This component shows a simple text tooltip.
 * @extends {Component}
 */
class Tooltip extends React.Component {
  constructor(props) {
    super(props);

    // Internal state to control when the tooltip is shown
    this.state = {
      isEnabled: props.enabled,
      isVisible: props.visible,
      activateOverlay: props.activateOverlay,
    };

    // Configure when the tooltip is activated
    // Possible values: click|hover|none
    this.trigger = props.trigger;

    // Small functions to control the tooltip visibility
    this.openTooltip = this.openTooltip.bind(this);
    this.closeTooltip = this.closeTooltip.bind(this);
    this.enableTooltip = this.enableTooltip.bind(this);
    this.renderCloseButton = this.renderCloseButton.bind(this);
    this.renderTooltip = this.renderTooltip.bind(this);
    this.renderOverlay = this.renderOverlay.bind(this);
    this.setOverlayMode = this.setOverlayMode.bind(this);

    // Attach all needed events. If the event configured is click, the only the onClick event is attached.
    this.getEventsToAttach = this.getEventsToAttach.bind(this);
  }

  /**
   * The tooltip can be updated when certain props change.
   * Currently, the supported changing props are `visible` and `enabled`.
   * If any of those props change, the local state is updated and a new render takes place.
   * @param {Object} prevProps
   */
  componentDidUpdate(prevProps) {
    const newProps = this.props;

    // Check for visibility
    if (newProps.visible !== prevProps.visible) {
      if (newProps.visible) {
        this.openTooltip();
      } else {
        this.closeTooltip();
      }
    }

    // Check for the enabled flag
    if (newProps.enabled !== prevProps.enabled) {
      this.enableTooltip(newProps.enabled);
    }

    // Check for the overlay activation
    if (newProps.activateOverlay !== prevProps.activateOverlay) {
      this.setOverlayMode(newProps.activateOverlay);
    }
  }

  /**
   * Attach only the needed events to the tooltip.
   * If the tooltip needs to be opened on a click event, then only the onClick handler will be attached.
   * If the tooltip needs to be opened on hover, two handlers are attached (onMouseOer and onMouseOut)
   *
   * @returns {Object} all the events handlers to attach to the tooltip
   */
  getEventsToAttach() {
    const events = {};

    switch (this.trigger) {
      case 'none':
        break;
      case 'click':
        events.onClick = this.openTooltip;
        break;
      case 'hover':
      default:
        events.onMouseOver = this.openTooltip;
        events.onMouseOut = this.closeTooltip;
        break;
    }

    return events;
  }

  /**
   * Enable/Disable the overlay to trigger events
   * @param {Boolean} value
   */
  setOverlayMode(value) {
    this.setState({
      activateOverlay: value,
    });
  }

  /**
   * Close the tooltip bubble
   * @param {Object} event If this method is called on an event handler, the event will be passed
   */
  closeTooltip(event) {
    /* If this method is called via an event handler, then we need to stop the propagation,
    to avoid side effects. For example, when clicking on the close button of a tooltip,
    the event will be processed by this function, but it will also be bubbled (the openTooltip method would
    catch the same event afterwards) */
    if (event) {
      event.stopPropagation();
    }

    this.setState({
      isVisible: false,
    });

    // Execute the close callback if provided
    this.props.onTooltipClose();
  }

  /**
   * Display the tooltip bubble
   */
  openTooltip() {
    this.setState({
      isVisible: true,
    });
  }

  /**
   * Enable/Disable the tooltip, including events
   * @param {Boolean} value
   */
  enableTooltip(value) {
    this.setState({
      isEnabled: value,
    });
  }

  /**
   * Render the tooltip elements
   * @returns {ReactElement} The tooltip react element
   */
  renderTooltip() {
    let data = null;

    if (this.state.isEnabled && this.state.isVisible) {
      data = (
        <div className='wtfTooltip' style={{ width: this.props.width }}>
          <span className='wtfTooltip_text'>{this.props.text}</span>
          { this.renderCloseButton() }
        </div>
      );
    }

    return data;
  }

  /**
   * Renders the close button that will be displayed inside the tooltip
   * @returns {ReactElement} The close button element
   */
  renderCloseButton() {
    let data = null;

    // The close button is only shown on `click` and `none` events
    if (this.trigger !== 'hover') {
      data = (
        <span
          className='wtfTooltip_close'
          onClick={this.closeTooltip}
          onKeyPress={this.closeTooltip}
          role='button'
          tabIndex={0}
        >
          &#x2715;
        </span>
      );
    }

    return data;
  }

  /**
   * Render the overlay with the configured events attached
   */
  renderOverlay() {
    let dataToRender = null;

    // Only render the overlay when needed.
    if (this.state.activateOverlay) {
      dataToRender = (
        <div
          className='wtfTooltip_overlay'
          {...this.getEventsToAttach()}
        />
      );
    }

    return dataToRender;
  }

  render() {
    return (
      <div
        className={`wtfTooltip_wrapper ${this.props.className}`}
        role='button'
        {...this.getEventsToAttach()}
      >
        { this.props.children }
        { this.renderTooltip() }
        { this.renderOverlay() }
      </div>
    );
  }
}

/**
 * Set the Tooltip component props default values.
 * @type {Object}
 */
Tooltip.defaultProps = {
  text: '',
  trigger: 'hover',
  visible: false,
  enabled: true,
  width: null,
  className: '',
  activateOverlay: false,
  onTooltipClose: () => {},
};

/**
 * Declare the Tooltip component PropTypes for better validation.
 * @type {Object}
 *
 * @property {String}   text            The text the tooltip displays.
 * @property {String}   trigger         The trigger event that will activate the tooltip.
 *                                      The possible values are click, hover or none.
 * @property {Boolean}  visible         Controls whether the tooltip is visible or hidden.
 * @property {Boolean}  enabled         Enable/disable the tooltip. No events are triggered when it is disabled.
 * @property {String}   width           The width of the tooltip bubble.
 * @property {String}   className       An optional className to be attached to the Tooltip wrapper.
 * @property {Boolean}  activateOverlay Enables an overlay which triggers the configured events.
 *                                      The overlay can be useful when the tooltip needs to be activated on disabled
 *                                      elements. For example, on FF, disabled elements do not trigger any events, so
 *                                      this overlay would be the emitter.
 * @property {Function} onTooltipClose  A callback to execute when the tooltip is closed (clicking on the close icon).
 */
Tooltip.propTypes = {
  text: PropTypes.string,
  trigger: PropTypes.string,
  visible: PropTypes.bool,
  enabled: PropTypes.bool,
  width: PropTypes.string,
  className: PropTypes.string,
  activateOverlay: PropTypes.bool,
  onTooltipClose: PropTypes.func,
};

export default Tooltip;



// WEBPACK FOOTER //
// ./src/components/common/Tooltip/Tooltip.js